import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type {
  Batch,
  BatchOrigination,
  CreateBatchRequest,
  SplitBatchRequest,
} from "../../domain/entities/batch";

export interface BatchMetadata {
  vendors: { id: string; name: string }[];
  manufacturers: { id: string; name: string }[];
}

export interface BatchDataSource {
  getBatches(options: QueryOptions): Promise<PaginatedResponse<Batch[]>>;
  getAllBatches(options: QueryOptions): Promise<PaginatedResponse<Batch[]>>;
  getBatch(id: string): Promise<Batch>;
  createBatch(request: CreateBatchRequest): Promise<Batch>;
  splitBatch(id: string, request: SplitBatchRequest): Promise<Batch>;
  deleteBatch(id: string): Promise<void>;
  downloadDevices(id: string): Promise<Blob>;
  downloadSelectedBatchesDevices(ids: string[]): Promise<Blob>;
  getMetadata(): Promise<BatchMetadata>;
  getBatchOrigination(id: string): Promise<BatchOrigination[]>;
}

export const batchDataSourceKey: InjectionKey<BatchDataSource> =
  Symbol("BatchDataSource");

export class BatchDataSourceImpl implements BatchDataSource {
  async getBatches(options: QueryOptions): Promise<PaginatedResponse<Batch[]>> {
    const response = await $api<PaginatedResponse<Batch[]>>(
      "/products/batches",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async getAllBatches(
    options: QueryOptions,
  ): Promise<PaginatedResponse<Batch[]>> {
    const response = await $api<PaginatedResponse<Batch[]>>(
      "/products/batches/all",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async getBatch(id: string): Promise<Batch> {
    const response = await $api<{ data: Batch }>(`/products/batches/${id}`, {
      method: "GET",
    });
    return response.data;
  }

  async createBatch(request: CreateBatchRequest): Promise<Batch> {
    const response = await $api<{ data: Batch }>("/products/batches", {
      method: "POST",
      body: request,
    });
    return response.data;
  }

  async deleteBatch(id: string): Promise<void> {
    await $api(`/products/batches/${id}`, {
      method: "DELETE",
    });
  }

  async splitBatch(id: string, request: SplitBatchRequest): Promise<Batch> {
    const response = await $api<{ data: Batch }>(
      `/products/batches/${id}/split`,
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async downloadDevices(id: string): Promise<Blob> {
    const response = await $api<Blob>(
      `/products/batches/${id}/devices/download`,
      {
        method: "GET",
        responseType: "blob" as "json",
      },
    );
    return response;
  }

  async downloadSelectedBatchesDevices(ids: string[]): Promise<Blob> {
    const response = await $api<Blob>("/products/batches/download", {
      method: "POST",
      body: { batch_ids: ids },
      responseType: "blob" as "json",
    });
    return response;
  }

  async getMetadata(): Promise<BatchMetadata> {
    const response = await $api<{ data: BatchMetadata }>("/products/metadata", {
      method: "GET",
    });
    return response.data;
  }

  async getBatchOrigination(id: string): Promise<BatchOrigination[]> {
    const response = await $api<{ data: BatchOrigination[] }>(
      `/products/batches/${id}/origination`,
      {
        method: "GET",
      },
    );
    return response.data;
  }
}
